import {Checkbox, IconButton, Theme} from '@mui/material'
import {FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useGetIdentity, useLocale, useTranslate} from 'react-admin'
import {Navigate} from 'react-router-dom'
import {makeStyles} from 'tss-react/mui'

import {SandboxContext} from '../../../contexts/SandboxContext'
import {GroupedChatMessage} from '../../../hooks/useGroupIncomingMediaMessages'
import useIsDesktop from '../../../hooks/useIsDesktop'
import useLongPressEvent from '../../../hooks/useLongPressEvent'
import hexToRgb from '../../../theme/hexToRgb'
import {WhatsappMessageTypesEnum} from '../../../types/graphqlSchema'
import displayChatMessageStatus from '../../../utils/displayChatMessageStatus'
import {isNativeMobileApp} from '../../../utils/platform'
import {ShareIcon} from '../../icons'
import {ChatContext} from '../ChatContext'
import ChatMessageBubble from './ChatMessageBubble'

const ChatMessageListItem: FC<ChatMessageListItemProps> = (
  {isShowingTranslations, onOpenTemplateDialog, onResendChatMessage, value}
) => {
  const {isSandbox} = useContext(SandboxContext)
  const {
    bulkAction,
    chatMessageId: selectedChatMessageId,
    onSetBulkAction,
    onToggleIsChatMessageSelected,
    selectedChatMessages,
    setCurrentlyRepliedChatMessage,
  } = useContext(ChatContext)
  const isSelected = useMemo(() => {
    if (!!value.groupedMessages) {
      return value.groupedMessages.every(msg => selectedChatMessages.has(msg.id))
    }
    return selectedChatMessages.has(value.id)
  }, [selectedChatMessages, value])
  const onClickChatMessage = useCallback(() => {
    if (!!value.groupedMessages) {
      value.groupedMessages.forEach(msg => onToggleIsChatMessageSelected(msg))
    }
    else {
      onToggleIsChatMessageSelected(value)
    }
  }, [onToggleIsChatMessageSelected, value])
  const canSelectChatMessage = useMemo(
    () => (
      bulkAction === 'FORWARD' ||
      (bulkAction === 'DOWNLOAD' && !!value.chatMessageFileAttachments.length)
    ),
    [bulkAction, value.chatMessageFileAttachments]
  )
  const translate = useTranslate()
  const locale = useLocale()
  const {identity: {id: userId} = {}} = useGetIdentity()
  const {cancelTouch, onTouchEnd, onTouchStart} = useLongPressEvent({
    onEnter: () => {
      onSetBulkAction('FORWARD')
      onClickChatMessage()
    },
  })
  const {
    authorUser,
    chat: {channelId, isProductNews},
    chatMessageTranslations,
    chatMessageWhatsappMessages,
    text,
  } = value
  const isRight = channelId ? !authorUser.customerUser : (authorUser.id === userId)
  const displayText = useMemo(() => {
    if (
      chatMessageWhatsappMessages?.[0]?.whatsappMessage.type ===
        WhatsappMessageTypesEnum.Unknown
    ) {
      return translate('chat.chatMessageBubble.unknownWhatsappMessageTypeText')
    }
    if (isProductNews && !isRight) {
      return chatMessageTranslations.find(t => t.language === locale)?.text ?? text
    }
    return text
  }, [
    chatMessageTranslations,
    chatMessageWhatsappMessages,
    isProductNews,
    isRight,
    locale,
    text,
    translate,
  ])
  const isDesktop = useIsDesktop()
  const [isHighlighted, setIsHighlighted] = useState(false)
  const bubbleBlinkTimeoutId = useRef<NodeJS.Timeout>()
  const makeChatMessageBubbleBlink = useCallback(() => {
    setIsHighlighted(true)
    clearTimeout(bubbleBlinkTimeoutId.current)
    bubbleBlinkTimeoutId.current = setTimeout(() => setIsHighlighted(false), 2500)
  }, [])
  useEffect(() => {
    if (selectedChatMessageId !== value.id) return
    makeChatMessageBubbleBlink()
    return () => {
      clearTimeout(bubbleBlinkTimeoutId.current)
      setIsHighlighted(false)
    }
  }, [makeChatMessageBubbleBlink, selectedChatMessageId, value])
  const isSingleMediaMessage = useMemo(
    () => (
      value.chatMessageFileAttachments.length === 1 &&
      (/(image|video)\//).test(value.chatMessageFileAttachments[0].mimeType)
    ),
    [value]
  )
  const {classes: styles} = useStyles({
    canSelectChatMessage,
    isHighlighted,
    isSelected,
    isSingleMediaMessage,
  })
  // TODO: temporary fix to redirect user to 404 page incase a chat_message does not
  //       contain a chat due to permission check failure
  if (!value?.chat) return <Navigate to="/*" />
  return (
    <div
      className={styles.container}
      onClick={() => bulkAction && canSelectChatMessage && onClickChatMessage()}
    >
      {bulkAction && (
        <Checkbox
          className={styles.selectChatMessageCheckbox}
          onClick={e => {
            e.stopPropagation()
            onClickChatMessage()
          }}
          checked={isSelected}
        />
      )}
      <li
        className={`${styles.root} ${isRight ? styles.right : styles.left}`}
        id={value.id}
        onDoubleClick={() => setCurrentlyRepliedChatMessage(value)}
        onTouchEnd={e => {
          // Enable context menu when the user releases the touch
          window.oncontextmenu = () => true
          onTouchEnd(e)
        }}
        onTouchMoveCapture={cancelTouch}
        onTouchStart={e => {
          // Disable context menu for touch screen events
          window.oncontextmenu = event => (event as PointerEvent).pointerType !== 'touch'
          onTouchStart(e)
        }}
      >
        <ChatMessageBubble
          displayText={displayText}
          isRight={isRight}
          isShowingTranslations={isShowingTranslations || isRight}
          onDoubleClick={e => e.stopPropagation()}
          onResendChatMessage={onResendChatMessage}
          onOpenTemplateDialog={onOpenTemplateDialog}
          status={displayChatMessageStatus(value)}
          value={value}
        />
        {(
          channelId &&
          !isSandbox &&
          !bulkAction &&
          (isDesktop || (!isDesktop && isSingleMediaMessage))
        ) && (
          <IconButton
            className={styles.forwardButton}
            onClick={() => {
              onSetBulkAction('FORWARD')
              onClickChatMessage()
            }}
            onDoubleClick={e => e.stopPropagation()}
          >
            <ShareIcon color="secondary" />
          </IconButton>
        )}
      </li>
    </div>
  )
}

const useStyles = makeStyles<MakeStyleProps, 'forwardButton'>()((
  theme, {canSelectChatMessage, isHighlighted, isSelected, isSingleMediaMessage}, classes
) => ({
  container: {
    background: isSelected ? theme.palette.background.secondary : 'unset',
    display: 'flex',
    gap: theme.remSpacing(1),
    paddingLeft: theme.remSpacing(2),
    paddingRight: theme.remSpacing(2),
    ...(isNativeMobileApp && {userSelect: 'none'}),
  },
  forwardButton: {
    [theme.breakpoints.down('md')]: {
      display: isSingleMediaMessage ? 'initial' : 'none',
    },
    marginBottom: theme.remSpacing(3),
    marginLeft: theme.remSpacing(1),
    marginRight: theme.remSpacing(1),
  },
  left: getDirectionStyle('left', theme, classes),
  right: getDirectionStyle('right', theme, classes),
  root: {
    [theme.breakpoints.up('lg')]: {
      [`&:not(:hover) > .${classes.forwardButton}`]: {
        display: 'none',
      },
    },
    '& + &': {
      marginTop: theme.remSpacing(1),
    },
    alignItems: 'center',
    backgroundColor: isHighlighted ?
      `rgba(${hexToRgb(theme.palette.chatBubble.main)}, 0.5)` : 'unset',
    color: '#525f7f',
    display: 'flex',
    paddingBottom: '.25rem',
    paddingTop: '.25rem',
    // Make anchor link go some pixels above where it's linked to.
    // Tells the browser to scroll a bit up by some pixels above the chat message bubble
    // when the user clicks on the chat message replied bubble anchor.
    scrollMarginTop: theme.remSpacing(6),
    transition: 'background-color 0.5s ease-out',
    width: '100%',
  },
  selectChatMessageCheckbox: {
    alignSelf: 'center',
    marginBottom: theme.remSpacing(3.5),
    visibility: canSelectChatMessage ? 'visible' : 'hidden',
  },
}))

const getDirectionStyle = (
  direction: string, theme: Theme, classes: Record<string, string>
): Record<string, any> => ({
  [`& + .${direction === 'right' ? classes.right : classes.left}`]: {
    display: 'none',
    marginTop: theme.remSpacing(30),
  },
  flexDirection: `row${direction === 'right' ? '-reverse' : ''}`,
  justifyContent: `${direction}`,
})

interface ChatMessageListItemProps {
  isShowingTranslations?: boolean
  onResendChatMessage: VoidFunction
  onOpenTemplateDialog: VoidFunction,
  value: GroupedChatMessage
}

interface MakeStyleProps {
  canSelectChatMessage?: boolean
  isHighlighted?: boolean
  isSelected?: boolean
  isSingleMediaMessage?: boolean
}

export default ChatMessageListItem
